<!--
ChannelFormModal — create/edit a franchisor_channels row (S13 Full-only
console). Registered centrally in ModalManager.vue and opened via eventBus
'modal.channelForm.open', optionally with { row } for edit.

`api_key` is WRITE-ONLY (docs/billing-franchisor-channels.md + SaveInput
docblock): the row this modal is handed always carries the MASKED form
('****1234' or null), never the raw secret. That masked value is shown as a
PLACEHOLDER only — the field itself always starts blank on open, and blank
on submit means "keep the stored secret unchanged". This modal never retains
a raw key beyond the single POST that sends it.

`provider` is filtered by `type` (email → resend, sms → mobizon per
docs/billing-franchisor-channels.md decision 2) but stays a real SELECT, not
a derived constant — forward-provisioning adds providers to PROVIDER_OPTIONS
without touching this component's structure.

The "Enviar teste" section only appears once the channel has an id (a brand
new, unsaved channel has nothing to test against) — it POSTs a REAL delivery
via testSend() and renders delivered/detail verbatim, per surface spec.
Um template pode ser escolhido (opcional): o envio passa a usar o texto real,
renderizado pelo mesmo caminho da pré-visualização (TemplatePreview).
-->
<template>
  <modal
    elem-id="modal_channel_form"
    :title="isEdit ? t('Editar canal') : t('Novo canal')"
    :show="isVisible"
    size="medium"
    :close-label="t('Fechar')"
    :save-label="t('Salvar')"
    :save-fn="handleSave"
    :save-disabled-fn="() => isSaving || isFormInvalid()"
    save-test-id="channel-form-submit"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <div class="channel-form-fields">
        <input-text
          v-model="name"
          elem-id="channel-form-name"
          :label="t('Nome')"
          :ng-required="true"
          data-testid="channel-form-name-input"
        />

        <select-list
          v-model="type"
          elem-id="channel-form-type"
          :label="t('Tipo')"
          :itens-select="typeOptions"
          :auto-select-first="false"
          :ng-required="true"
          data-testid="channel-form-type-select"
          @update:model-value="onTypeChange"
        />

        <select-list
          v-model="provider"
          elem-id="channel-form-provider"
          :label="t('Provedor')"
          :itens-select="providerOptions"
          :auto-select-first="false"
          :ng-required="true"
          data-testid="channel-form-provider-select"
        />

        <!-- Opcional no SMS, necessário na prática no e-mail: o Mobizon roteia
             pelo originador dele quando nenhum é informado, enquanto um e-mail
             sem endereço de origem não é um e-mail válido. Dizer isso no rótulo
             é o que evita alguém inventar um sender id para "consertar" um canal
             que nunca esteve quebrado. -->
        <input-text
          v-model="sender"
          elem-id="channel-form-sender"
          :label="type === 'sms' ? t('Sender ID (opcional)') : t('E-mail de origem (from)')"
          data-testid="channel-form-sender-input"
        />

        <input-password
          v-model="apiKey"
          elem-id="channel-form-api-key"
          :label="t('Credencial (API key)')"
          :placeholder="apiKeyPlaceholder"
          data-testid="channel-form-api-key-input"
        />
        <p class="channel-form-hint" data-testid="channel-form-api-key-hint">
          {{ t('Deixe em branco para manter a credencial já salva.') }}
        </p>

        <input-toggle
          v-model="enabled"
          elem-id="channel-form-enabled"
          :label="t('Ativo')"
          data-testid="channel-form-enabled-toggle"
        />
      </div>

      <div v-if="isEdit" class="channel-form-test-send" data-testid="channel-form-test-send-section">
        <h4 class="channel-form-test-send-title">{{ t('Enviar teste') }}</h4>
        <input-text
          v-model="testTarget"
          elem-id="channel-form-test-target"
          :label="type === 'sms' ? t('Telefone de destino') : t('E-mail de destino')"
          data-testid="channel-form-test-target-input"
        />

        <!-- Opcional: sem template escolhido, o teste manda o texto fixo de
             sempre. Só aparecem os templates DO TIPO deste canal — o backend
             recusa 'template_channel_type_mismatch' de qualquer forma, mas
             oferecer a opção impossível é convidar o erro. -->
        <select-list
          v-if="testTemplateOptions.length > 0"
          v-model="testTemplateId"
          elem-id="channel-form-test-template"
          :label="t('Template (opcional)')"
          :itens-select="testTemplateOptions"
          :auto-select-first="false"
          data-testid="channel-form-test-template-select"
        />
        <Button
          :label="t('Enviar teste')"
          icon="pi pi-send"
          severity="secondary"
          :disabled="isTesting || testTarget.trim() === ''"
          data-testid="channel-form-test-send-btn"
          @click="handleTestSend"
        />

        <div v-if="testResult" class="channel-form-test-result" data-testid="channel-form-test-result">
          <span
            class="channel-form-test-result-badge"
            :class="testResult.delivered ? 'test-result-delivered' : 'test-result-failed'"
            data-testid="channel-form-test-result-badge"
          >
            {{ testResult.delivered ? t('Entregue') : t('Falhou') }}
          </span>
          <pre class="channel-form-test-result-detail" data-testid="channel-form-test-result-detail">{{ testResult.detail }}</pre>
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useFranchisorChannels } from '../../../composables/useFranchisorChannels'
import type { FranchisorChannels_ChannelRow } from '../../../contracts/FranchisorChannels_ChannelRow'
import type { FranchisorChannels_TestSendResponseDados } from '../../../contracts/FranchisorChannels_TestSendResponse'
import { FRANCHISOR_TRIGGER_LABELS } from './franchisorTriggerLabels'

const { t } = useI18n()
const { saveChannel, testSend, templates } = useFranchisorChannels()

type ChannelType = 'email' | 'sms' | ''

const PROVIDER_OPTIONS: Record<Exclude<ChannelType, ''>, Array<{ ID: string; Name: string }>> = {
  email: [{ ID: 'resend', Name: 'Resend' }],
  sms: [{ ID: 'mobizon', Name: 'Mobizon' }],
}

const isVisible = ref(false)
const isSaving = ref(false)
const isTesting = ref(false)

const id = ref<number | null>(null)
const name = ref('')
const type = ref<ChannelType>('')
const provider = ref('')
const sender = ref('')
const apiKey = ref('')
const apiKeyMasked = ref('')
const enabled = ref(true)

const testTarget = ref('')
const testTemplateId = ref<string>('')
const testResult = ref<FranchisorChannels_TestSendResponseDados | null>(null)

const isEdit = computed(() => id.value !== null)

const typeOptions = [
  { ID: 'email', Name: t('E-mail') },
  { ID: 'sms', Name: t('SMS') },
]

const providerOptions = computed(() => (type.value !== '' ? PROVIDER_OPTIONS[type.value] : []))

/**
 * Templates of this channel's type, labelled by trigger. Reads the
 * composable's module-scope list, already loaded by FranchisorChannelsPage —
 * the modal does not fetch, so opening it costs nothing extra.
 */
const testTemplateOptions = computed(() =>
  templates.value
    .filter((tpl) => tpl.channel_type === type.value)
    .map((tpl) => ({
      ID: String(tpl.id),
      Name: t(FRANCHISOR_TRIGGER_LABELS[tpl.trigger_key ?? ''] ?? (tpl.trigger_key ?? '')),
    })),
)

const apiKeyPlaceholder = computed(() => apiKeyMasked.value || t('Nenhuma credencial salva'))

function isFormInvalid(): boolean {
  if (name.value.trim() === '') return true
  if (type.value === '') return true
  if (provider.value === '') return true
  return false
}

/**
 * Changing type re-derives provider from PROVIDER_OPTIONS — a stale
 * provider from a previous type selection would send a mismatched pair the
 * backend has no route for.
 */
function onTypeChange(newType: string): void {
  const t2 = newType as ChannelType
  provider.value = t2 !== '' ? (PROVIDER_OPTIONS[t2]?.[0]?.ID ?? '') : ''
}

function resetFields(): void {
  id.value = null
  name.value = ''
  type.value = ''
  provider.value = ''
  sender.value = ''
  apiKey.value = ''
  apiKeyMasked.value = ''
  enabled.value = true
  testTarget.value = ''
  testTemplateId.value = ''
  testResult.value = null
}

function openModal(payload?: { row: FranchisorChannels_ChannelRow }): void {
  log('ChannelFormModal', 'openModal', 'Abrindo modal de canal', { edit: !!payload?.row })
  resetFields()
  const row = payload?.row
  if (row) {
    id.value = row.id
    name.value = row.name ?? ''
    type.value = (row.type as ChannelType) ?? ''
    provider.value = row.provider ?? ''
    sender.value = row.sender ?? ''
    apiKeyMasked.value = row.api_key ?? ''
    enabled.value = row.enabled ?? false
  }
  isVisible.value = true
}

function closeModal(): void {
  isVisible.value = false
  resetFields()
}

async function handleSave(): Promise<void> {
  if (isFormInvalid()) return
  isSaving.value = true
  try {
    const row = await saveChannel({
      ...(id.value != null ? { id: id.value } : {}),
      name: name.value.trim(),
      type: type.value as string,
      provider: provider.value,
      enabled: enabled.value,
      ...(sender.value.trim() !== '' ? { sender: sender.value.trim() } : {}),
      ...(apiKey.value.trim() !== '' ? { api_key: apiKey.value.trim() } : {}),
    })
    log('ChannelFormModal', 'handleSave', 'Canal salvo', { id: row.id })
    eventBus.emit('globalMessage.success', t('Canal salvo com sucesso'))
    eventBus.emit('channelForm.saved', { row })
    closeModal()
  } catch (err) {
    log('ChannelFormModal', 'handleSave', 'Erro ao salvar canal', err)
    eventBus.emit('globalMessage.error', t('Erro ao salvar canal') + ': ' + ((err as Error)?.message ?? ''))
  } finally {
    isSaving.value = false
  }
}

async function handleTestSend(): Promise<void> {
  if (id.value === null || testTarget.value.trim() === '') return
  isTesting.value = true
  testResult.value = null
  try {
    const templateId = testTemplateId.value !== '' ? Number(testTemplateId.value) : null
    const result = await testSend({
      channel_id: id.value,
      target: testTarget.value.trim(),
      ...(templateId !== null ? { template_id: templateId } : {}),
    })
    testResult.value = result
    log('ChannelFormModal', 'handleTestSend', 'Teste de envio concluído', { delivered: result.delivered })
  } catch (err) {
    log('ChannelFormModal', 'handleTestSend', 'Erro ao enviar teste', err)
    eventBus.emit('globalMessage.error', t('Erro ao enviar teste') + ': ' + ((err as Error)?.message ?? ''))
  } finally {
    isTesting.value = false
  }
}

onMounted(() => {
  eventBus.on('modal.channelForm.open', openModal)
})

onBeforeUnmount(() => {
  eventBus.off('modal.channelForm.open', openModal)
})
</script>

<style scoped>
.channel-form-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.channel-form-hint {
  margin: -0.5rem 0 0 0;
  font-size: 0.8rem;
  color: var(--p-text-muted-color, #6b7280);
}

.channel-form-test-send {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--p-content-border-color, #e5e7eb);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.channel-form-test-send-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.channel-form-test-result {
  padding: 0.75rem;
  border-radius: 8px;
  background: var(--p-content-background, #f9fafb);
}

.channel-form-test-result-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.test-result-delivered {
  background: #d1fae5;
  color: #065f46;
}

.test-result-failed {
  background: #fee2e2;
  color: #991b1b;
}

.channel-form-test-result-detail {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.82rem;
  margin: 0;
}
</style>
